import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import {
  type DistanceUnit as CoreDistanceUnit,
  type CurrencyCode,
  getOrgCurrency,
  parseValue,
  useFormat,
} from '@brightdrop/localization-core';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { useOrganizationDetails } from '~/common/contexts/OrganizationDetailsContext';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';
import { formatCurrency, formatNumber } from '~/common/utils/number.utils';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';

import type { DiagnosticValueFormat } from './diagnosticFormat';

/**
 * Fraction digits already present on a percent, so formatting does not round it.
 * `99.6` stays one digit; a whole number stays zero.
 */
const fractionDigits = (value: number): number => {
  if (!Number.isFinite(value) || Number.isInteger(value)) {
    return 0;
  }
  const text = value.toString();
  const dot = text.indexOf('.');
  if (dot < 0 || text.includes('e') || text.includes('E')) {
    return 0;
  }
  return text.length - dot - 1;
};

/**
 * Formats checkout and catalog amounts in the organization currency when
 * localization is on; otherwise uses legacy {@link formatCurrency}.
 *
 * An acting-profile currency wins, including while organization details are
 * still loading. Otherwise the organization-details currency is used after
 * that request finishes. A missing code renders as {@link DEFAULT_EMPTY_VALUE}.
 */
export const useOrganizationMoney = (): ((value: number) => string) => {
  // `/me` types this as a string. The organization record enums the same codes.
  const profileCurrency = useSelector(selectActingProfileOrganization)
    ?.currency as CurrencyCode | undefined;
  const { organization, isLoading } = useOrganizationDetails();
  const { currency } = getOrgCurrency({
    profileCurrency,
    detailsCurrency: organization?.currency,
    detailsLoading: isLoading,
  });
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useCallback(
    (value: number): string => {
      const formatted = localizationEnabled
        ? format.money(value, { code: currency })
        : formatCurrency(value);

      return formatted || DEFAULT_EMPTY_VALUE;
    },
    [currency, format, localizationEnabled]
  );
};

/**
 * A percent shown as the cell already shows it, with no extra rounding.
 *
 * Flag-off returns `` `${value}%` ``. Flag-on keeps the same digits, so `99.6`
 * stays `99.6%` in en-US and a whole `85` is still `85%`. Use
 * {@link useLocalizedPercent} when the cell has already truncated, rounded, or
 * mapped the number.
 */
export const useLocalizedShownPercent = (): ((value: number) => string) => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useCallback(
    (value: number): string =>
      localizationEnabled
        ? format.number(value / 100, {
            notation: 'percent',
            digits: fractionDigits(value),
          })
        : `${value}%`,
    [format, localizationEnabled]
  );
};

/**
 * A whole-number percent the endpoint sent as 0–100, such as `85`.
 *
 * Flag-off returns `` `${value}%` ``. Flag-on divides by 100 and formats with
 * zero fraction digits, so the reader's locale supplies the percent sign and
 * the space in front of it. Callers must round first: these digits round, and
 * 99.6 would otherwise read as 100.
 */
export const useLocalizedPercent = (): ((value: number) => string) => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useCallback(
    (value: number): string =>
      localizationEnabled
        ? format.number(value / 100, { notation: 'percent', digits: 0 })
        : `${value}%`,
    [format, localizationEnabled]
  );
};

/**
 * Integer counts such as vehicles and devices.
 * Flag-off calls {@link formatNumber}. Flag-on calls `format.number` with
 * `{ digits: 0 }`, so a non-integer still renders as an integer.
 */
export const useLocalizedCount = (): ((value: number) => string) => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useCallback(
    (value: number): string =>
      localizationEnabled
        ? format.number(value, { digits: 0 })
        : formatNumber(value),
    [format, localizationEnabled]
  );
};

/**
 * Integer counts whose flag-off text is bare `String(n)`, with no grouping.
 * Flag-on matches {@link useLocalizedCount}.
 */
export const useLocalizedIntegerCount = (): ((value: number) => string) => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useCallback(
    (value: number): string =>
      localizationEnabled ? format.number(value, { digits: 0 }) : String(value),
    [format, localizationEnabled]
  );
};

/**
 * The locale a chart library formats its own numbers in, such as Chart.js
 * tooltip values. Undefined while localization is off, so the library keeps
 * its default of the browser's locale.
 */
export const useLocalizedChartLocale = (): string | undefined => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return localizationEnabled ? format.locale() : undefined;
};

/**
 * A distance, converted from the unit the payload named.
 * Uses the reader's preferences from {@link useFormat}; no target unit argument.
 */
export const useLocalizedDistance = (): ((
  value: number,
  valueUnit: CoreDistanceUnit
) => string) => {
  const format = useFormat();

  return useCallback(
    (value: number, valueUnit: CoreDistanceUnit): string =>
      format.distance.format(value, { valueUnit, context: 'compact' }),
    [format]
  );
};

/**
 * Last communicated, on the core's recency ladder.
 * Undefined while localization is off, so the Luxon ladder stays in place.
 */
export const useLocalizedRecency = ():
  | ((iso: string) => string | undefined)
  | undefined => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  const formatter = useCallback(
    (iso: string): string | undefined => {
      // A missing offset is UTC. `new Date(iso)` would read it as host-local.
      const parsed = parseValue(iso);
      return parsed ? format.recency(parsed.date) : undefined;
    },
    [format]
  );

  return localizationEnabled ? formatter : undefined;
};

/**
 * A `YYYY-MM-DD` date. Undefined while localization is off, so the caller
 * keeps its own legacy date string.
 */
export const useLocalizedPlainDate = ():
  | ((iso: string) => string)
  | undefined => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  const formatter = useCallback(
    (iso: string): string => format.plainDate(new Date(iso)),
    [format]
  );

  return localizationEnabled ? formatter : undefined;
};

/**
 * A timestamp with its zone, for the hidden Added column.
 * Undefined while localization is off.
 */
export const useLocalizedDateTime = ():
  | ((iso: string) => string | undefined)
  | undefined => {
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  const formatter = useCallback(
    (iso: string): string | undefined => {
      const parsed = parseValue(iso);
      return parsed
        ? format.dateTime(parsed.date, { withZone: true })
        : undefined;
    },
    [format]
  );

  return localizationEnabled ? formatter : undefined;
};

/**
 * Formatters diagnostics columns share.
 * Distance always comes from localization-core; percent/recency/dateTime follow the flag.
 */
export const useDiagnosticValueFormat = (): DiagnosticValueFormat => {
  const formatPercent = useLocalizedPercent();
  const formatShownPercent = useLocalizedShownPercent();
  const distance = useLocalizedDistance();
  const recency = useLocalizedRecency();
  const dateTime = useLocalizedDateTime();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));

  return useMemo(
    () => ({
      distance,
      percent: localizationEnabled ? formatPercent : formatShownPercent,
      recency: localizationEnabled ? recency : undefined,
      dateTime: localizationEnabled ? dateTime : undefined,
    }),
    [
      dateTime,
      distance,
      formatPercent,
      formatShownPercent,
      localizationEnabled,
      recency,
    ]
  );
};
